import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Plus, Search } from "lucide-react";
import type { Mcc } from "../../../features/purposeCode/PurposeCodeSlice";

interface MccMappingAddSectionProps {
  purposeCode: string;
  allMccs: Mcc[];
  existingMappings: Record<string, string>;
  mappingUpdateLoading: boolean;
  mappingUpdateError?: string | null;
  onAddMapping: (mcc: string) => void | Promise<void>;
}

const MccMappingAddSection: React.FC<MccMappingAddSectionProps> = ({
  purposeCode,
  allMccs,
  existingMappings,
  mappingUpdateLoading,
  mappingUpdateError,
  onAddMapping,
}) => {
  const [selectedMcc, setSelectedMcc] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const availableOptions = useMemo(
    () =>
      allMccs
        .filter(m => !existingMappings[m.mcc])
        .sort((a, b) => a.mcc.localeCompare(b.mcc)),
    [allMccs, existingMappings],
  );

  const filteredAvailableOptions = useMemo(() => {
    const term = dropdownSearch.trim().toLowerCase();
    if (!term) return availableOptions;
    return availableOptions.filter(
      m =>
        m.mcc.toLowerCase().includes(term) ||
        m.description.toLowerCase().includes(term),
    );
  }, [availableOptions, dropdownSearch]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen]);

  const handleAddClick = async () => {
    const value = selectedMcc.trim();
    if (!value) {
      setLocalError("Select an MCC code to add.");
      return;
    }

    setLocalError(null);
    await onAddMapping(value);
    setSelectedMcc("");
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="flex-1">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Add MCC mapping for{" "}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-700">
            {purposeCode}
          </span>
        </label>
        <p className="text-[11px] text-gray-500 mb-1">
          Choose an existing Merchant Category Code to associate with this purpose code.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative w-full" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => {
                if (mappingUpdateLoading || availableOptions.length === 0) return;
                setDropdownOpen(open => !open);
              }}
              className="w-full min-h-[44px] flex items-center justify-between px-3 py-2 border border-gray-200 rounded-md bg-white shadow-sm hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 text-left"
              disabled={mappingUpdateLoading || availableOptions.length === 0}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {selectedMcc
                    ? `${selectedMcc} — ${
                        availableOptions.find(m => m.mcc === selectedMcc)?.description || ""
                      }`
                    : availableOptions.length === 0
                    ? "All MCCs are already mapped to this purpose code"
                    : "Select an MCC code"}
                </p>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-500 ml-2 transition-transform ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {dropdownOpen && availableOptions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                <div className="px-2 py-2 border-b border-gray-100">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={dropdownSearch}
                      onChange={e => setDropdownSearch(e.target.value)}
                      placeholder="Search MCC or description..."
                      className="w-full h-8 pl-7 pr-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>
                <div className="max-h-32 overflow-auto p-1">
                  {filteredAvailableOptions.map(m => {
                    const isSelected = selectedMcc === m.mcc;
                    return (
                      <button
                        key={m.mcc}
                        type="button"
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm hover:bg-gray-50 ${
                          isSelected ? "bg-blue-50 text-blue-800" : "text-gray-700"
                        }`}
                        onClick={() => {
                          setSelectedMcc(m.mcc);
                          setDropdownOpen(false);
                          if (localError) setLocalError(null);
                        }}
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className="truncate">
                          {m.mcc} — {m.description}
                        </span>
                      </button>
                    );
                  })}
                  {filteredAvailableOptions.length === 0 && (
                    <div className="px-3 py-3 text-xs text-gray-500 text-center">
                      No MCC codes match your search
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleAddClick}
            disabled={
              mappingUpdateLoading ||
              !selectedMcc.trim() ||
              availableOptions.length === 0
            }
            className="sm:w-auto w-full px-3 py-2 text-xs rounded-md border border-cardborder bg-brand text-white hover:bg-brand/90 disabled:opacity-60 whitespace-nowrap inline-flex items-center justify-center gap-1"
          >
            {mappingUpdateLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            <span>Add mapping</span>
          </button>
        </div>
        {(localError || mappingUpdateError) && (
          <p className="mt-2 text-[11px] text-red-600">
            {localError || mappingUpdateError}
          </p>
        )}
      </div>
    </div>
  );
};

export default MccMappingAddSection;

